import {
  Button,
  Card,
  CardFooter,
  CardHeader,
  Tag,
} from '@fluentui/react-components';
import { ArrowRightRegular } from '@fluentui/react-icons';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { makeDocsLink } from '@tdp/common/constants';
import { Flex, StyledText } from '@tdp/common/fluentV9MigrationComponents';
import { useCommand, useSelector } from '@tdp/core';

import { useAppValidationHeroCardStyle } from './AppValidationDetails.styles';
import { OverviewHeroCard } from './OverviewHeroCard';
import { OverviewHeroCardShimmer } from './OverviewHeroCardShimmer';
import { appDetailsSlice } from '../../appDetails/slice';
import { ValidationStatus } from '../../appValidation/abstractions/EAppValidation';
import { IAppValidationList } from '../../appValidation/abstractions/IAppValidation';
import { ValidationInfoInner } from '../../appValidation/components/ValidationInfo';
import { appValidationSlice } from '../../appValidation/redux/slice';
import { formatDateIntl24 } from '../charts/utils';

export const AppValidationHeroCard: React.FC = () => {
  const intl = useIntl();
  const styles = useAppValidationHeroCardStyle();
  const dispatch = useDispatch();

  const { appId } = useParams();
  const navigate = useNavigate();
  const app = useSelector((s) =>
    appDetailsSlice.selectors.getAppById(s, appId ?? '')
  );
  const getValidationsList = useCommand(
    (s) => s.appValidationCommands.getValidationsList
  );
  const getValidationDetails = useCommand(
    (s) => s.appValidationCommands.getValidationDetails
  );

  // const app: IAppDefinition = useAppSelector((state: any) => state.apps.AppDefinition);
  const validationList: IAppValidationList = useSelector(
    (state) => state.appValidation.validationList
  );
  // const validation: IAppValidationItem | null = useSelector((state) => state.validationV2.validationDetail);
  const validation = useSelector((s) =>
    appValidationSlice.selectors.currentValidation(s)
  );

  const manifestVersion = app ? app.manifestVersion : null;
  const appVersion = app ? app.version : null;

  useEffect(
    () => () => {
      // dispatch(ResetAppValidationState(dispatch));
      dispatch(appValidationSlice.actions.reset());
    },
    []
  );

  useEffect(() => {
    if (!validationList.appValidations) {
      getValidationsList({ appId: app.teamsAppId });
    }
  }, [validationList]);

  useEffect(() => {
    if (
      validationList.appValidations &&
      validationList.appValidations.length >= 1
    ) {
      getValidationDetails({
        validationId: validationList.appValidations[0].id ?? '',
      });
      // dispatch(GetAppValidationDetail(dispatch, validationList.appValidations[0].id));
    }
  }, [validationList]);

  const appValidationDocumentationLink = makeDocsLink(
    'microsoftteams/platform/concepts/build-and-test/manage-your-apps-in-developer-portal#publish'
  );

  const showAppManifest = intl.formatMessage({
    defaultMessage: 'Manifest',
    description: 'Shows app manifest in manifest viewer.',
  });

  const appFlightAppVersion = intl.formatMessage({
    defaultMessage: 'App version',
    description: 'appFlightAppVersion',
  });

  const learnMoreSansEllipsis = intl.formatMessage({
    defaultMessage: 'Learn more',
    description: 'Learn more link text without the ellipsis',
  });

  const appValidationRecent = intl.formatMessage({
    defaultMessage: 'Recent app validation',
    description: 'Title Text for Recent app validation',
  });

  const recommendationTitle = intl.formatMessage({
    defaultMessage: 'Recommendation',
    description: 'Recommendation card title',
  });

  const appValidationTableCompletedAt = intl.formatMessage({
    defaultMessage: 'Completed at',
    description: 'appValidationTableCompletedAt',
  });

  const appValidationHeroNew = intl.formatMessage({
    defaultMessage:
      'Check your app package against the test cases Microsoft uses when reviewing your app. Resolve errors or warnings before submitting your app.',
    description: 'Title text for Hero Card for App Validation Section',
  });

  const appValidationHeroInProg = intl.formatMessage({
    defaultMessage:
      'Your latest app validation request is in progress. You can keep working on other tasks in the developer portal without any interruption.',
    description: 'Title text for Hero Card for App Validation Section',
  });

  const getStartedTitle = intl.formatMessage({
    defaultMessage: 'Get started',
    description: 'getStartedTitle',
  });

  const status = intl.formatMessage({
    defaultMessage: 'Status',
    description: 'status',
  });

  const subtitle = intl.formatMessage({
    defaultMessage: 'App validation',
    description:
      'Title Text for Async App Validation, this will appear on the Navigation Group',
  });

  if (validationList.appValidations === null) {
    return <AppValidationHeroCardShimmer />;
  }

  if (validationList.appValidations.length === 0) {
    return (
      <AppValidationHeroCardNoResponse
        title={recommendationTitle}
        imgSrc={'/images/illustrations/validation-empty.png'}
        buttonText={getStartedTitle}
        message={appValidationHeroNew}
        subtitle={subtitle}
        secondaryText={learnMoreSansEllipsis}
        secondaryAction={() => {
          window.open(appValidationDocumentationLink, '_blank');
        }}
        action={() => {
          //  TODO: (astripa) Log BI telemetry
          // Logger.log(TelemetryObjectId.AppValidationHeroNewValidation, {});
          navigate(`/apps/${app.teamsAppId}/app-validation`);
        }}
      />
    );
  }

  if (
    validation?.status === ValidationStatus.Created ||
    validation?.status === ValidationStatus.InProgress
  ) {
    return (
      <AppValidationHeroCardNoResponse
        title={recommendationTitle}
        imgSrc={'/images/illustrations/validation-wait.png'}
        buttonText={status}
        message={appValidationHeroInProg}
        subtitle={subtitle}
        action={() => {
          //  TODO: (astripa) Log BI telemetry
          // Logger.log(TelemetryObjectId.AppValidationHeroStatus, {});
          navigate(
            `/apps/${app.teamsAppId}/app-validation/${validation.appValidationId ?? ''}`
          );
        }}
      />
    );
  }

  if (validation && validation.status === ValidationStatus.Completed) {
    return (
      <Card style={{ width: '100%' }}>
        <CardHeader>
          <StyledText weight="bold">{appValidationRecent}</StyledText>
        </CardHeader>
        <div style={{ marginTop: '42px' }}>
          <ValidationInfoInner currentValidation={validation} />
          <Flex className={styles.heroMargin}>
            <Tag size="small">
              {
                <StyledText>
                  {appValidationTableCompletedAt
                    .concat(': ')
                    .concat(
                      validation.updatedAt &&
                      formatDateIntl24(validation.updatedAt, intl)
                    )}
                </StyledText>
              }
            </Tag>
            <Tag size="small">
              {
                <StyledText>
                  {manifestVersion
                    ? showAppManifest.concat(': ').concat(manifestVersion)
                    : showAppManifest}
                </StyledText>
              }
            </Tag>
            <Tag size="small">
              {
                <StyledText>
                  {appVersion
                    ? appFlightAppVersion.concat(': ').concat(appVersion)
                    : appFlightAppVersion}
                </StyledText>
              }
            </Tag>
          </Flex>
        </div>
        <CardFooter>
          <Button
            className={styles.learnMoreButton}
            appearance="primary"
            // TODO: (astripa) check for transparant button styling
            onClick={() => {
              // TODO: (astripa) Log BI telemetry
              // Logger.log(TelemetryObjectId.AppValidationHeroNavigateToValidation, {});
              navigate(
                `/apps/${app.teamsAppId}/app-validation/${validation.appValidationId ?? ''}`
              );
            }}
          >
            <Flex>
              <StyledText content={learnMoreSansEllipsis} />{' '}
              <ArrowRightRegular className={styles.iconMargin} />
            </Flex>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return <AppValidationHeroCardShimmer />;
};

const AppValidationHeroCardShimmer: React.FC = () => (
  <OverviewHeroCardShimmer />
);

const AppValidationHeroCardNoResponse: React.FC<{
  title: string;
  subtitle: string;
  imgSrc: string;
  message: string;
  buttonText: string;
  secondaryText?: string;
  secondaryAction?: () => void;
  action: () => void;
}> = ({
  title,
  subtitle,
  imgSrc,
  message,
  buttonText,
  secondaryText,
  action,
  secondaryAction,
}) => (
    <OverviewHeroCard
      title={title}
      subtitle={subtitle}
      imgSrc={imgSrc}
      message={message}
      buttonText={buttonText}
      secondaryText={secondaryText}
      action={action}
      secondaryAction={secondaryAction}
    />
  );
